import { BUSINESS_TYPES_WITHOUT_PRODUCTS } from "@/common/fields/business-types";
import {
    SAMPLE_PRODUCTS,
    sampleLinksSegment
} from "@/common/lib/onboarding-preview-samples";

const STEP_ROUTE_MAP = {
    slug: "/onboarding/multitenant",
    "business-type": "/onboarding/multitenant/business-type",
    branding: "/onboarding/multitenant/branding",
    "social-networks": "/onboarding/multitenant/social-networks",
    "custom-links": "/onboarding/multitenant/custom-links",
    plan: "/onboarding/multitenant/plan",
    products: "/onboarding/multitenant/products"
};

const COMPLETION_ROUTE = "/onboarding/multitenant/welcome";

// Igual para cualquier tipo de negocio: `products` aparece solo si el plan
// elegido lo incluye (lo decide el backend en `flow.steps`).
const FULL_STEP_SEQUENCE = [
    "slug",
    "business-type",
    "branding",
    "social-networks",
    "plan",
    "custom-links",
    "products"
];

const getters = {
    stepRoute: (state) => {
        if (!state.onboardingFlow) return null;
        return STEP_ROUTE_MAP[state.onboardingFlow.step] || null;
    },
    routeForStep: () => (step) => {
        return STEP_ROUTE_MAP[step] || null;
    },
    completionRoute: () => COMPLETION_ROUTE,
    isStepLocked: (state) => (step) => {
        const entry = state.completedSteps.find((item) => item.step === step);
        return entry ? Boolean(entry.locked) : false;
    },
    isStepCompleted: (state) => (step) => {
        return state.completedSteps.some((item) => item.step === step);
    },
    reachableSteps: (state) => {
        const steps = state.completedSteps.map((item) => item.step);
        const flow = state.onboardingFlow;
        if (flow) {
            [flow.step, flow.nextStep].forEach((step) => {
                if (step && !steps.includes(step)) {
                    steps.push(step);
                }
            });
        }
        return steps;
    },
    orderedSteps: (state, allGetters) => {
        const known = new Set(allGetters.reachableSteps);
        const flow = state.onboardingFlow;
        const flowSteps = flow && Array.isArray(flow.steps) ? flow.steps : [];
        // El orden lo manda el backend (`flow.steps`): cambia según el tipo
        // de negocio y los flujos viejos conservan el suyo.
        if (flowSteps.length) {
            const steps = flowSteps
                .map((item) => (typeof item === "string" ? item : item?.step))
                .filter((step) => FULL_STEP_SEQUENCE.includes(step));
            known.forEach((step) => {
                if (!steps.includes(step)) steps.push(step);
            });
            return steps;
        }
        return FULL_STEP_SEQUENCE;
    },
    // Fuente unica del preview en vivo para branding/social-networks/
    // custom-links/products: reemplaza el `previewCatalog` que cada pagina
    // armaba por su cuenta combinando `selections` con estado local.
    previewCatalog: (state, _getters, rootState) => {
        const draft = state.onboardingDraft;
        // Los enlaces de ejemplo solo mientras no haya pasado por el paso de
        // enlaces: si lo pasó sin crear ninguno, la vista previa va sin ellos.
        const linksStepDone = (
            (state.onboardingFlow && state.onboardingFlow.completedSteps) ||
            []
        ).some((item) => item.step === "custom-links");
        const businessType =
            state.selections?.businessType || state.businessType;
        const segments = draft.customLinks.length
            ? [
                  {
                      id: "live-custom-links",
                      type: "links",
                      style: "block",
                      links: draft.customLinks.map((link) => ({
                          id: link.id,
                          url: link.url,
                          title: link.title,
                          imageUrl: link.image?.url
                      }))
                  }
              ]
            : linksStepDone
            ? []
            : [sampleLinksSegment(businessType)];
        // Profesional y Organización arrancan en `feed` y no usan productos:
        // la vista previa no les muestra los de ejemplo.
        const withoutProducts =
            BUSINESS_TYPES_WITHOUT_PRODUCTS.includes(businessType);
        return {
            businessName: rootState.auth?.tenantName || "Tu negocio",
            businessDescription: draft.businessDescription,
            logoIconUrl: draft.logoIconUrl,
            stackColors: draft.stackColors,
            socialNetworks: draft.socialNetworks,
            whatsappNumber: draft.whatsappNumber,
            segments,
            products: withoutProducts
                ? []
                : draft.products.length
                ? draft.products
                : SAMPLE_PRODUCTS,
            displayMode: withoutProducts ? "feed" : draft.displayMode
        };
    }
};

export default getters;
